/**
 * Client-side mirror of the backend git org scope check.
 *
 * The backend is authoritative (`repo_in_org_scope`); this exists so the UI can
 * avoid dead ends, e.g. offering a GitHub App install for a repo the workspace
 * can never attach. Fails OPEN when the workspace has no git org set, matching
 * the backend.
 *
 * @param repoName Full `owner/repo` path.
 * @param gitOrgName The workspace's `GitOrgName` (may be comma-separated for
 *   legacy multi-org rows).
 */
export const isRepoInOrgScope = (
  repoName?: string,
  gitOrgName?: string
): boolean => {
  if (!repoName || !repoName.includes('/')) return true;
  const tokens = (gitOrgName ?? '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;
  return tokens.includes(repoName.split('/')[0].trim().toLowerCase());
};

/**
 * Extract the `owner/repo` path from a provider repo URL.
 *
 * Returns an empty string when the URL has fewer than two path segments, which
 * `isRepoInOrgScope` treats as unattributable and therefore allowed. GitLab
 * subgroups keep their full path, so only the first segment is compared against
 * the scope, which matches the backend.
 */
export const repoPathFromUrl = (repoUrl?: string): string => {
  if (!repoUrl) return '';
  try {
    const path = new URL(repoUrl).pathname
      .replace(/\.git$/, '')
      .split('/')
      .filter(Boolean);
    return path.length >= 2 ? path.join('/') : '';
  } catch {
    return '';
  }
};
